import React, {useCallback, useEffect, useState} from "react";

import AmendmentGroup from "./amendment_group";
import type {
  OnLogAmendmentErrors,
  ScrollToAmendmentClause,
} from "./amendment_group";

import {Clausepart} from "common/types/document";
import {
  AppliedAmendments,
  ClauseChangeError,
  DocumentAmendment,
  ProcessedAmendment,
  ProcessedClauseChange,
  PreviewHeading,
} from "common/flowmaster/types/amendment";
import type {SubAmendmentFailure} from "word/taskpane/routes/checklist/utils/apply_document_amendments";

export type ApplyDocumentAmendmentsFn = (
  amendments: DocumentAmendment[],
  onFail: (amendment: DocumentAmendment, errorMessage?: string) => void,
  onPartialFail: (
    amendment: DocumentAmendment,
    failures: SubAmendmentFailure[],
  ) => void,
  onSuccess: (amendment: DocumentAmendment) => void,
) => Promise<unknown>;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
} as const;

interface AmendmentsPanelProps {
  amendments: ProcessedAmendment[];
  applyClauseChanges: (changes: ProcessedClauseChange[]) => void;
  appliedAmendments: AppliedAmendments;
  clauseChangeErrors: ClauseChangeError[];
  setClauseChangeError?: (clauseChangeId: string, message: string) => void;
  documentClauseparts: Clausepart[];
  documentHeadings?: PreviewHeading[];
  stacked?: boolean;
  defaultExpanded?: boolean;
  defaultShowChanges?: boolean;
  onOpenConversation?: (conversationId: number) => void;
  activeConversationId?: number | null;
  applyDocumentAmendments?: ApplyDocumentAmendmentsFn;
  getIsTrackAllChangesOn?: () => Promise<boolean>;
  enableTrackAllChanges?: () => Promise<void>;
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  bulkApplyingIds?: string[];
  onViewedConversationIdChange?: (id: number | null) => void;
  removeAppliedAmendments?: (amendmentIds: string[]) => void;
  scrollToClause?: ScrollToAmendmentClause;
}

const AmendmentsPanel: React.FC<AmendmentsPanelProps> = ({
  amendments,
  applyClauseChanges,
  appliedAmendments,
  clauseChangeErrors,
  setClauseChangeError,
  documentClauseparts,
  documentHeadings,
  stacked = false,
  defaultExpanded = false,
  defaultShowChanges = false,
  onOpenConversation,
  activeConversationId,
  applyDocumentAmendments,
  getIsTrackAllChangesOn,
  enableTrackAllChanges,
  onLogAmendmentErrors,
  bulkApplyingIds,
  onViewedConversationIdChange,
  removeAppliedAmendments,
  scrollToClause,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const viewedConversationId = amendments[currentIndex]?.conversationId ?? null;

  useEffect(() => {
    if (onViewedConversationIdChange) {
      onViewedConversationIdChange(viewedConversationId);
      return () => onViewedConversationIdChange(null);
    }
  }, [viewedConversationId, onViewedConversationIdChange]);

  const navigate = useCallback(
    (direction: number) => {
      setCurrentIndex(
        prev => (prev + direction + amendments.length) % amendments.length,
      );
    },
    [amendments.length],
  );

  if (stacked) {
    return (
      <div style={styles.container}>
        {amendments.map((amendment, index) => (
          <AmendmentGroup
            key={index}
            amendment={amendment}
            applyClauseChanges={applyClauseChanges}
            appliedAmendments={appliedAmendments}
            clauseChangeErrors={clauseChangeErrors}
            setClauseChangeError={setClauseChangeError}
            documentClauseparts={documentClauseparts}
            documentHeadings={documentHeadings}
            defaultExpanded={defaultExpanded}
            defaultShowChanges={defaultShowChanges}
            onOpenConversation={onOpenConversation}
            activeConversationId={activeConversationId}
            applyDocumentAmendments={applyDocumentAmendments}
            getIsTrackAllChangesOn={getIsTrackAllChangesOn}
            enableTrackAllChanges={enableTrackAllChanges}
            onLogAmendmentErrors={onLogAmendmentErrors}
            bulkApplyingIds={bulkApplyingIds}
            removeAppliedAmendments={removeAppliedAmendments}
            scrollToClause={scrollToClause}
          />
        ))}
      </div>
    );
  }

  const currentAmendment = amendments[currentIndex];
  if (!currentAmendment) {
    return null;
  }

  return (
    <div style={styles.container}>
      <AmendmentGroup
        amendment={currentAmendment}
        applyClauseChanges={applyClauseChanges}
        appliedAmendments={appliedAmendments}
        clauseChangeErrors={clauseChangeErrors}
        setClauseChangeError={setClauseChangeError}
        documentClauseparts={documentClauseparts}
        documentHeadings={documentHeadings}
        currentIndex={currentIndex}
        totalAmendments={amendments.length}
        onNavigate={navigate}
        defaultExpanded={defaultExpanded}
        defaultShowChanges={defaultShowChanges}
        onOpenConversation={onOpenConversation}
        activeConversationId={activeConversationId}
        applyDocumentAmendments={applyDocumentAmendments}
        getIsTrackAllChangesOn={getIsTrackAllChangesOn}
        enableTrackAllChanges={enableTrackAllChanges}
        onLogAmendmentErrors={onLogAmendmentErrors}
        bulkApplyingIds={bulkApplyingIds}
        removeAppliedAmendments={removeAppliedAmendments}
        scrollToClause={scrollToClause}
      />
    </div>
  );
};

export default AmendmentsPanel;
